import { BcxFormField, BcxFormValidation } from '@/models/BcxForm';
import useI18n from '@/mixins/useI18n';

const DOT_OR_COMMA_REGEX = /[.,]/;

export const isZip = (value:string) => (/\d{4,5}/).test(value);
export const isEmail = (value:string) => (/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i).test(value);

export const useFormValidation = () => {
  const { t } = useI18n();

  const validateField = (value: string, field: BcxFormField, data:Record<string, string>) => {
    let errorMessage = '';
    if (field.required && !value?.trim()) {
      errorMessage = `${t('common.bcxFormValidations.required', { field: field.label })}`;
    } else {
      field?.validations?.some((validation) => {
        if (typeof validation === 'string') {
          switch (validation) {
            case BcxFormValidation.EMAIL:
              if (!isEmail(value)) errorMessage = `${t('common.bcxFormValidations.email', { field: field.label })}`;
              break;
            case BcxFormValidation.ZIP:
              if (!isZip(value)) errorMessage = `${t('common.bcxFormValidations.zip', { field: field.label })}`;
              break;
          }
        } else if (typeof validation === 'function') {
          errorMessage = validation(value, data) ?? '';
        }
        return !!errorMessage;
      });
    }
    return errorMessage;
  };

  const validateRate = (value: string) => {
    if (!value) {
      return t('common.bcxFormValidations.rate-empty').toString();
    }

    const valueParts = value.toString().split(DOT_OR_COMMA_REGEX);
    const MAX_DIGITS_BEFORE_DOT = 6;

    if (valueParts[0]?.length > MAX_DIGITS_BEFORE_DOT) {
      return t(
        'common.bcxFormValidations.rate-length',
        {
          max: MAX_DIGITS_BEFORE_DOT
        }
      ).toString();
    }

    const MAX_DIGITS_AFTER_DOT = 2;

    if (valueParts[1]?.length > MAX_DIGITS_AFTER_DOT) {
      return t(
        'common.bcxFormValidations.rate-length-decimals',
        {
          max: MAX_DIGITS_AFTER_DOT
        }
      ).toString();
    }

    if (+value < 1) {
      return t('common.bcxFormValidations.rate-min').toString();
    }

    return null;
  };

  const validateHrsPerWeek = (value: string) => {
    if (!value) {
      return t('common.bcxFormValidations.hrs-week').toString();
    }

    if (DOT_OR_COMMA_REGEX.test(value)) {
      // Only allow integers
      return t('common.bcxFormValidations.hrs-week-decimal').toString();
    }

    const hrsPerWeek = +value;

    const MAX_HOURS_PER_WEEK = 60;

    if (hrsPerWeek > MAX_HOURS_PER_WEEK) {
      return t('common.bcxFormValidations.hrs-week-max', {
        max: MAX_HOURS_PER_WEEK
      }).toString();
    }

    const MIN_HOURS_PER_WEEK = 1;

    if (hrsPerWeek < MIN_HOURS_PER_WEEK) {
      return t('common.bcxFormValidations.hrs-week-min', {
        min: MIN_HOURS_PER_WEEK
      }).toString();
    }

    return null;
  };

  return { validateField, validateRate, validateHrsPerWeek };
};
